import { storeFilters } from '@/vuex/modules/voc-items/voc-items.utils';
import { fetchMinimalUsers } from '@/vuex/modules/users/users.utils';
import { PerSubscriptionUserStorage } from '@utils/storage';
import { runAggregationOnce, SUB_PRODUCT_AREAS } from '@/utils/aggregation';
import { i18n_t as $t } from '@/utils/i18n';
import VocItemsSourcesAggregation from '@/voc/aggregations/voc-item-sources/voc-item-sources.aggregation';
import VocItemsCreatedByAdditionalUsersAggregation from '@/voc/aggregations/voc-item-created-by-visitors/voc-item-created-by-additional-users.aggregation';
import { formatLabels, formatVocItemFilterForAgg } from '@/voc/helpers/common.helper';
import { FEEDBACK_IMPORTANCES } from '@/voc/constants/constants';
import { npsFilters } from '@/net-promoter-system/constants/nps.constants';
import isEqual from 'lodash/isEqual';
import { defaultSelectedAccount } from '@/vuex/modules/global-filters/default-filters.utils';
import { convertConfigToTimeSeries } from '@/common/time-series-picker/time-series-picker.util';
import set from 'lodash/set';
import { hasSegmentFlag } from '@/data/flags/local-flags';

const { CAPTURE, VALIDATE } = SUB_PRODUCT_AREAS || {};

const isJestTest = !!process.env.JEST_WORKER_ID;

let sort = { order: 'none', prop: '' };

const state = {
    map: {},
    createdByList: undefined,
    choices: {
        feedbackStatuses: [],
        createdByUsers: [],
        sources: [],
        importance: FEEDBACK_IMPORTANCES,
        labels: [],
        linkedIdeas: [
            {
                id: true,
                label: 'Is linked to an Idea',
            },
            {
                id: false,
                label: 'Is not linked to an Idea',
            },
        ],
        alert: ['Blocker to Sale', 'High Frustration', 'Churn Risk'],
        npsScores: npsFilters.rating,
        assignedToUsers: [],
        accountTypes: [],
        portals: [],
    },
    choicesLoaded: false,
    storedFiltersLoaded: false,
    type: undefined,
    emptyFilters: {
        feedbackExplore: {
            allTime: true,
            segmentId: 'everyone',
        },
        feedback: {
            groups: [],
            feedbackStatuses: [],
            createdBy: [],
            sources: [],
            importance: [],
            labels: [],
            linkedIdeas: [],
            appIds: ['*'],
            segmentId: 'everyone',
            segmentIdInvalid: false,
            allTime: true,
            timeSeries: isJestTest
                ? null
                : convertConfigToTimeSeries({ type: 'Last 30 Days', start: null, end: null }).config,
            searchText: '',
            account: '',
            npsScores: [],
            feedbackType: [],
            alert: [],
            assignee: [],
            accountTypes: [],
        },
        ideas: {
            groups: [],
            statuses: [],
            createdBy: [],
            importance: [],
            labels: [],
            workarounds: [],
            app: '*',
            segment: null,
            allTime: true,
            searchText: '',
            account: { account: defaultSelectedAccount },
            assignee: [],
            accountTypes: [],
            portals: [],
        },
    },
    emptyFiltersMinusGlobal: {
        feedbackExplore: {},
        feedback: {
            groups: [],
            feedbackStatuses: [],
            createdBy: [],
            sources: [],
            importance: [],
            labels: [],
            linkedIdeas: [],
            searchText: '',
            npsScores: [],
            assignee: [],
            accountTypes: [],
        },
        ideas: {
            groups: [],
            statuses: [],
            createdBy: [],
            workarounds: [],
            importance: [],
            labels: [],
            searchText: '',
            assignee: [],
            accountTypes: [],
            portals: [],
        },
    },
    promises: {},
    prompt: '',
};

const mutations = {
    setMap (state, { filters }) {
        state.map = filters;
    },
    updateMap (state, { key, value }) {
        if (key === 'allTime' && value === true && state.map.timeSeries) {
            delete state.map.timeSeries;
        } else if (
            key === 'allTime' &&
            value === false &&
            !state.map.timeSeries &&
            state.emptyFilters[state.type]?.timeSeries
        ) {
            state.map.timeSeries = state.emptyFilters[state.type].timeSeries;
        }

        state.map = {
            ...state.map,
            [key]: value,
        };
    },
    updateStoredFiltersLoaded (state, value) {
        state.storedFiltersLoaded = value;
    },
    setType (state, type) {
        state.type = type;
    },
    updateChoices (state, choices = {}) {
        state.choices = {
            ...state.choices,
            ...choices,
        };
    },
    setChoicesLoaded (state, value) {
        state.choicesLoaded = value;
    },
    setPromise (state, { promise, key }) {
        set(state.promises, key, promise);
    },
    setPrompt (state, { prompt }) {
        state.prompt = prompt;
    },
};

const actions = {
    loadAll ({ state, commit, dispatch, rootGetters }, { type = 'idea' } = {}) {
        commit('setType', type);
        commit('setChoicesLoaded', false);

        if (type === 'feedback' || type === 'feedbackExplore') {
            const filters = { ...state.emptyFilters[type] };

            if (filters.allTime) {
                delete filters.timeSeries;
            }

            commit('setMap', { filters });
            commit('updateStoredFiltersLoaded', true);
        } else {
            commit('updateStoredFiltersLoaded', false);

            return Promise.all([
                PerSubscriptionUserStorage.get('vocListExpansionFilters'),
                dispatch(
                    'userSettings/loadOne',
                    {
                        namespaced: true,
                        name: 'groupFilter',
                    },
                    { root: true },
                ),
            ]).then(([filters = {}]) => {
                const savedGroupFilter = rootGetters['groups/storedUserSettingGroupFilter'];

                commit('setMap', {
                    filters: {
                        ...(filters?.value || {}),
                        groups: savedGroupFilter,
                    },
                });
                commit('updateStoredFiltersLoaded', true);
            });
        }
    },
    loadChoices ({ commit, state, dispatch, rootGetters }, { type = 'idea' }) {
        if (state.promises.choices) {
            return state.promises.choices;
        }

        if (state.choicesLoaded && state.type === type) {
            return Promise.resolve();
        }

        VocItemsSourcesAggregation.subProductArea = type === 'idea' ? VALIDATE : CAPTURE;

        const basePromises = [
            fetchMinimalUsers(),
            runAggregationOnce(VocItemsSourcesAggregation, { type }).catch(() => {
                // supress the error so it doesn't break the UI
            }),
            dispatch('vocPortal/loadPortals', {}, { root: true }),
        ];

        const surveysPromise = hasSegmentFlag('sendSurveyResponsesToListen')
            ? dispatch('surveys/loadAll', {}, { root: true }).catch(() => {
                // supress the error so it doesn't break the UI
            })
            : Promise.resolve();

        const visitorsPromise =
            type === 'feedback'
                ? runAggregationOnce(VocItemsCreatedByAdditionalUsersAggregation, { type }).catch(() => {
                    // supress the error so it doesn't break the UI
                    return [];
                })
                : Promise.resolve([]);

        const promise = Promise.all([...basePromises, surveysPromise, visitorsPromise])
            .then(([minimalUsersList, sources, , , visitorsList]) => {
                const portals = rootGetters['vocPortal/portals'] || [];
                sources ??= [];

                if (hasSegmentFlag('sendSurveyResponsesToListen')) {
                    sources.forEach((source) => {
                        if (source.sourceCategory === 'Surveys' && source.sourceIdentifier) {
                            const survey = rootGetters['surveys/surveyById'](source.sourceIdentifier);
                            source.guideName = survey?.name || '(Unavailable Survey)';
                        }
                    });
                }

                const mappedUsers =
                    minimalUsersList?.map((user) => ({
                        id: user.id,
                        label: user.username,
                    })) || [];

                const createdByUsers =
                    type === 'feedback'
                        ? (() => {
                            const mappedVisitors =
                                  visitorsList?.map((visitor) => ({
                                      id: visitor.id,
                                      label: visitor.username ?? visitor.id,
                                      type: visitor.type,
                                  })) || [];

                            return [
                                {
                                    id: 'User',
                                    category: $t('filters.createdBy.userLabel'),
                                    users: [...mappedUsers.map((user) => ({ ...user, divided: false }))],
                                },
                                {
                                    id: 'Visitor',
                                    category: $t('filters.createdBy.visitor'),
                                    users: [...mappedVisitors.filter((visitor) => visitor.type === 'Visitor')],
                                },
                                {
                                    id: 'IntegrationKey',
                                    category: $t('filters.createdBy.integrationKey'),
                                    users: [...mappedVisitors.filter((visitor) => visitor.type === 'IntegrationKey')],
                                },
                            ];
                        })()
                        : mappedUsers;

                commit('updateChoices', {
                    createdByUsers,
                    sources,
                    assignedToUsers: mappedUsers,
                    portals: portals || [],
                });
                commit('setChoicesLoaded', true);
            })
            .finally(() => {
                commit('setPromise', { key: 'choices', promise: null });
            });

        commit('setPromise', { key: 'choices', promise });

        return promise;
    },
    updateFilter ({ commit, state, rootGetters, dispatch }, { key, value, persist = true, persistGlobal = false }) {
        const globalKeys = ['app', 'segment', 'timeSeries', 'allTime', 'account'];

        if (globalKeys.includes(key)) {
            if (persistGlobal) {
                switch (key) {
                    case 'app': {
                        // Team Spork feel free to delete this single app logic if not being used.
                        const selectedAppId = rootGetters['globalFilters/selectedApp']?.id;
                        if (!isEqual(selectedAppId, value)) {
                            const selectedApp = rootGetters['apps/getAppOrCategoryById'](value);
                            selectedApp.id = value === '*' ? '*' : selectedApp.id;

                            commit(
                                'globalFilters/setSelectedApp',
                                {
                                    selectedApp,
                                    updateAngular: true,
                                },
                                { root: true },
                            );
                        }
                        break;
                    }
                    case 'segment': {
                        const selectedSegmentId = rootGetters['globalFilters/segmentId'];
                        if (!isEqual(selectedSegmentId, value)) {
                            commit(
                                'globalFilters/setSelectedSegmentId',
                                {
                                    selectedSegmentId: value,
                                    fromVue: true,
                                },
                                { root: true },
                            );
                        }
                        break;
                    }
                    case 'timeSeries': {
                        const selectedTimeSeries = rootGetters['globalFilters/timeSeries'];
                        if (value && !isEqual(selectedTimeSeries, value)) {
                            commit(
                                'globalFilters/setTimeSeries',
                                {
                                    timeSeries: value,
                                    fromVue: true,
                                },
                                { root: true },
                            );
                        }
                        break;
                    }
                    case 'allTime': {
                        const selectedAllTime = rootGetters['globalFilters/allTime'];
                        if (!isEqual(selectedAllTime, value)) {
                            commit(
                                'globalFilters/setAllTime',
                                {
                                    allTime: value,
                                },
                                { root: true },
                            );
                        }
                        break;
                    }
                    case 'account': {
                        const selectedAccount = rootGetters['globalFilters/selectedAccountAccount'];
                        if (!isEqual(selectedAccount, value?.account)) {
                            commit(
                                'globalFilters/setSelectedAccount',
                                {
                                    selectedAccount: value,
                                    updateAngular: true,
                                },
                                { root: true },
                            );
                        }
                        break;
                    }
                }
            }
            commit('updateMap', { key, value });
        } else if (key === 'groups' && !isEqual(state.map[key], value)) {
            commit('updateMap', { key, value });
        } else if (!isEqual(state.map[key], value) && value !== undefined) {
            const storedFilters = Object.keys(state.map)
                .filter((key) => key !== 'groups')
                .reduce((cur, key) => {
                    return Object.assign(cur, { [key]: state.map[key] });
                }, {});

            const filters = {
                ...storedFilters,
                [key]: value,
            };

            commit('updateMap', { key, value });

            if (persist) storeFilters({ filters });
            if(key === 'searchText') {
                const currentSort = rootGetters['vocItems/table/tableConfig'].sort;

                if(currentSort?.order !== 'none') {
                    sort = currentSort;
                }

                if(value.length > 0) {
                    dispatch('vocItems/table/update', { sort: { prop: currentSort.prop, order: 'none' } }, { root: true });
                } else {
                    dispatch('vocItems/table/update', { sort: { prop: sort.prop ?? currentSort.prop, order: sort.order ?? currentSort.order } }, { root: true });
                }
            }
        }
    },
    updateFilters ({ dispatch }, { filters, persist = true, persistGlobal = true, reset = false }) {
        if (reset) {
            dispatch('reset', { persist, persistGlobal });
        }
        Object.keys(filters).forEach((key) => {
            dispatch('updateFilter', { key, value: filters[key], persist, persistGlobal });
        });
    },
    updatePrompt ({ commit }, { prompt }) {
        commit('setPrompt', { prompt });
    },
    reset (
        { commit, dispatch, state },
        { persist = false, persistGlobal = false } = { persist: false, persistGlobal: false },
    ) {
        if (Object.keys(state.emptyFiltersMinusGlobal[state.type] || {}).length) {
            dispatch('updateFilters', { filters: state.emptyFiltersMinusGlobal[state.type], persist, persistGlobal });
        } else {
            commit('setMap', { filters: {} });
        }
    },
};

const getters = {
    filters: (state) => state.map || {},
    filtersForAgg: (_state, getters) =>
        Object.entries(getters.filters).reduce((processedFilters, [key, filter]) => {
            const formattedFilter = formatVocItemFilterForAgg(key, filter);

            if (formattedFilter) {
                processedFilters[formattedFilter.key] = formattedFilter.value;
            }

            return processedFilters;
        }, {}),
    allFilters: (state, _getters, rootState, rootGetters) => ({
        ...state.map,
        ...(state.type === 'ideas' && {
            segment: rootGetters['globalFilters/segmentId'],
            timeSeries: rootGetters['globalFilters/timeSeries'],
            allTime: rootGetters['globalFilters/allTime'],
            app: rootState.globalFilters.selectedApp?.id,
            account: rootState.globalFilters.selectedAccount,
        }),
    }),
    emptyFilters: (state) => state.emptyFilters[state.type],
    feedbackEmptyFilters: (state) => state.emptyFilters.feedback,
    feedbackExploreEmptyFilters: (state) => state.emptyFilters.feedbackExplore,
    filterByKey: (state) => (filterKey) => state.map[filterKey],
    choices: (state, _getters, rootState, rootGetters) => ({
        ...state.choices,
        feedbackStatuses: rootGetters['statuses/list']('feedback').map((status) => {
            return {
                id: status.id,
                name: status.name,
                icon: {
                    type: 'circle',
                    size: '8',
                    fill: status.style.dotColor,
                    stroke: 0,
                    class: 'status-circle',
                },
            };
        }),
        labels: formatLabels(rootGetters['voc/labels/feedbackLabels']),
    }),
    prompt: (state) => state.prompt,
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
